import { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AdminInput, AdminTextarea } from "../../../../../components/adminForm";
import {
  createSalesChannel,
  updateSalesChannel,
} from "../../../../../api/salesChannels";
import {
  requestTableRefresh,
  updateFloatPanel,
} from "../../../../../reducers/userConfigSlice";
export const Details = () => {
  const data =
    useSelector((state) => state.aplicationConfig.userConfig.floatPanel.data) ||
    {};
  const isEmpty = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel.isEmpty,
  );
  const catalogs =
    useSelector((state) => state.aplicationConfig.corporationData.catalogs) ||
    [];
  const [form, setForm] = useState({
    type: data.type || "TPV",
    name: data.name || "",
    identifier: data.identifier || "",
    address: data.address || "",
    phone: data.phone || "",
    email: data.email || "",
    description: data.description || "",
    catalogId: data.catalogId || "",
    pricePercentage: data.pricePercentage ?? "0",
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false);
  const pending = useRef(false),
    identifierEdited = useRef(!isEmpty),
    formRef = useRef(null);
  const dispatch = useDispatch();
  const change = (key, value) => {
    setSaved(false);
    setForm((previous) => ({
      ...previous,
      [key]: value,
      ...(key === "name" && !identifierEdited.current
        ? {
            identifier: value
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-|-$/g, ""),
          }
        : {}),
    }));
  };
  const save = async (event) => {
    event.preventDefault();
    if (pending.current || !formRef.current.reportValidity()) return;
    pending.current = true;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const payload = { ...form, catalogId: form.catalogId || null };
      const response = isEmpty
        ? await createSalesChannel(payload)
        : await updateSalesChannel(data.salesChannelId || data.id, payload);
      if (response?.error !== false) throw Error();
      dispatch(requestTableRefresh());
      setSaved(true);
      if (isEmpty)
        dispatch(
          updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
        );
    } catch {
      setError(
        "No se pudo guardar el canal. Revisa los datos y el identificador; tus cambios se conservan.",
      );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return (
    <form ref={formRef} className="channelFormGrid" onSubmit={save}>
      <fieldset disabled={busy} className="channelCreationFields">
      <label>Tipo de canal
        <select aria-label="Tipo de canal" value={form.type} disabled={busy || !isEmpty} onChange={event => {
          const type = event.target.value;
          setForm(previous => ({ ...previous, type, ...(type === "ONLINE" && !previous.name ? { name: "Venta online", identifier: "venta-online" } : {}) }));
        }}>
          <option value="TPV">TPV · venta en mostrador</option>
          <option value="ONLINE">Venta online · Shop</option>
        </select>
        <small>{form.type === "ONLINE" ? "La Shop utiliza este canal, su catálogo y su variación de precio. Solo puede haber uno por negocio." : "Canal para terminales de la aplicación TPV."}</small>
      </label>
      <AdminInput
        id="channel-name"
        label="Nombre del canal"
        required
        value={form.name}
        onChange={(value) => change("name", value)}
        description="Nombre que aparecerá en los pedidos."
      />
      <AdminInput
        id="channel-identifier"
        label="Identificador"
        required
        value={form.identifier}
        onChange={(value) => {
          identifierEdited.current = true;
          change("identifier", value);
        }}
        description="Se completa con el nombre. Puedes personalizarlo."
      />
      <div className="wide">
        <AdminTextarea
          id="channel-description"
          label="Descripción"
          defaultValue={form.description}
          onChange={(event) => change("description", event.target.value)}
        />
      </div>
      <AdminInput
        id="channel-phone"
        label="Teléfono de contacto"
        type="tel"
        required={form.type === "TPV"}
        defaultValue={form.phone}
        onChange={(value) => change("phone", value)}
      />
      <AdminInput
        id="channel-email"
        label="Correo de contacto"
        type="email"
        required={form.type === "TPV"}
        defaultValue={form.email}
        onChange={(value) => change("email", value)}
      />
      <div className="wide">
        <AdminInput
          id="channel-address"
          label="Dirección"
          defaultValue={form.address}
          onChange={(value) => change("address", value)}
        />
      </div>
      <label>
        Catálogo
        <select
          aria-label="Catálogo del canal"
          value={form.catalogId}
          onChange={(event) => change("catalogId", event.target.value)}
        >
          <option value="">
            {isEmpty ? "Catálogo por defecto" : "Sin catálogo asignado"}
          </option>
          {catalogs.map((item) => (
            <option key={item.catalogId} value={item.catalogId}>
              {item.name}
            </option>
          ))}
        </select>
        <small>Los productos disponibles proceden de este catálogo.</small>
      </label>
      <AdminInput
        id="channel-price-percentage"
        type="number"
        required
        min={-100}
        step="any"
        label="Variación de precio (%)"
        defaultValue={form.pricePercentage}
        onChange={(value) => change("pricePercentage", value)}
        description="0 mantiene el precio. El porcentaje se aplica a producto, variante y extras antes del IVA; no cambia pedidos ya confirmados."
      />
      <div className="channelSaveBar">
        {error && <p role="alert">{error}</p>}
        {saved && <p role="status">Canal guardado</p>}
        <button type="submit" disabled={busy} aria-busy={busy}>
          {busy ? "Guardando…" : "Guardar canal"}
        </button>
      </div>
      </fieldset>
    </form>
  );
};
